/**
 * useSubmitForm — handles form submission with client-side validation.
 *
 * Follows FERP hook pattern: consumes service singleton, manages React state.
 * Validates form data against ExposedWorkflow schema before submitting.
 */
import { useState, useCallback } from 'react';
import type { ExposedWorkflow, FormSubmission } from '../types';
import { portalFormService, type ValidationError } from '../services/PortalFormService';

interface UseSubmitFormReturn {
  submit: (data: Record<string, unknown>) => Promise<FormSubmission | null>;
  isSubmitting: boolean;
  submission: FormSubmission | null;
  validationErrors: ValidationError[];
  error: Error | null;
  reset: () => void;
}

export function useSubmitForm(
  portalSlug: string,
  workflowSlug: string,
  workflow: ExposedWorkflow | null,
): UseSubmitFormReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submission, setSubmission] = useState<FormSubmission | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [error, setError] = useState<Error | null>(null);

  const submit = useCallback(
    async (data: Record<string, unknown>): Promise<FormSubmission | null> => {
      setError(null);
      setValidationErrors([]);

      if (workflow) {
        const errors = portalFormService.validateFormData(workflow, data);
        if (errors.length > 0) {
          setValidationErrors(errors);
          return null;
        }
      }

      setIsSubmitting(true);
      try {
        const result = await portalFormService.submitForm(portalSlug, workflowSlug, data);
        setSubmission(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [portalSlug, workflowSlug, workflow],
  );

  const reset = useCallback(() => {
    setSubmission(null);
    setValidationErrors([]);
    setError(null);
  }, []);

  return { submit, isSubmitting, submission, validationErrors, error, reset };
}
